import axios from './axios-instance';
import config from './config';
import { B2bModal } from 'epic.b2b_modal';
import ModalNetworkProblem from './ModalNetworkProblem.vue';
import ModalSessionExpired from './ModalSessionExpired.vue';
import { ensurePullGuard } from './pull-guard';
import { startNetworkMonitor } from './network-monitor';
import { ensureBxAjaxGuard } from './bx-ajax-guard';
import { ensureSessionGuard } from './session-guard';
import { ensureXhrGuard, disableXhrGuard } from './xhr-guard';

const scope = typeof globalThis !== 'undefined'
	? globalThis
	: typeof window !== 'undefined'
		? window
		: typeof self !== 'undefined'
			? self
			: {};

const epicNamespace = scope.EPIC || (scope.EPIC = {});

epicNamespace.Network = epicNamespace.Network || {};
epicNamespace.Network.http = axios;
epicNamespace.Network.config = config;
epicNamespace.Network.disableXhrGuard = disableXhrGuard;

const bx = scope && scope.BX ? scope.BX : null;

if (bx && typeof bx.namespace === 'function') {
	bx.namespace('EPIC.Network');

	bx.EPIC = bx.EPIC || {};
	bx.EPIC.Network = bx.EPIC.Network || {};
	bx.EPIC.Network.http = axios;
	bx.EPIC.Network.config = config;
	bx.EPIC.Network.disableXhrGuard = disableXhrGuard;
}

ensurePullGuard();
startNetworkMonitor();
ensureBxAjaxGuard();
ensureSessionGuard();
ensureXhrGuard();

let offlineModalInstance = null;
let sessionExpiredModalInstance = null;
const networkIssueState = {
	count: 0,
	timer: null
};

const resetNetworkIssueState = () => {
	networkIssueState.count = 0;

	if (networkIssueState.timer) {
		clearTimeout(networkIssueState.timer);
		networkIssueState.timer = null;
	}
};

const scheduleNetworkIssueReset = () => {
	if (!config.networkEventWindow) return;

	if (networkIssueState.timer) clearTimeout(networkIssueState.timer);

	networkIssueState.timer = setTimeout(resetNetworkIssueState, config.networkEventWindow);
};

const openOfflineModal = () => {
	if (window.location.pathname.toString().startsWith('/terminal')) return;

	if (sessionExpiredModalInstance) return sessionExpiredModalInstance;

	if (offlineModalInstance) return offlineModalInstance;

	offlineModalInstance = new B2bModal({
		component:     ModalNetworkProblem,
		componentName: 'ModalNetworkProblem',
		params:        {},
		events:        {
			onAfterClose: () => {
				offlineModalInstance = null;
			}
		},
		popupOptions: {
			width:      530,
			titleBar:   false,
			closeIcon:  false,
			closeByEsc: false,
			autoHide:   false,
			blocking:   true
		}
	});

	return offlineModalInstance;
};

const openSessionExpiredModal = () => {
	if (sessionExpiredModalInstance) return sessionExpiredModalInstance;

	sessionExpiredModalInstance = new B2bModal({
		component:     ModalSessionExpired,
		componentName: 'ModalSessionExpired',
		params:        {},
		popupOptions: {
			width:      530,
			titleBar:   false,
			closeIcon:  false,
			closeByEsc: false,
			autoHide:   false,
			blocking:   true,
			events:     {
				onAfterClose: () => {
					sessionExpiredModalInstance = null;
				}
			}
		}
	});

	return sessionExpiredModalInstance;
};

const closeOfflineModal = () => {
	if (!offlineModalInstance) return;

	offlineModalInstance.close();
	offlineModalInstance = null;
};

const handleNetworkIssueEvent = event => {
	if (offlineModalInstance || sessionExpiredModalInstance) return;

	const weight = event.type === 'epic:network:offline'
		? config.networkEventOfflineWeight
		: 1;

	networkIssueState.count += weight;
	scheduleNetworkIssueReset();

	if (networkIssueState.count >= config.networkEventThreshold) {
		resetNetworkIssueState();
		openOfflineModal(event);
	}
};

const handleOnlineEvent = () => {
	resetNetworkIssueState();
	closeOfflineModal();
};

const handleSessionExpiredEvent = () => {
	resetNetworkIssueState();
	closeOfflineModal();
	openSessionExpiredModal();
};

[
	'epic:network:offline',
	'epic:network:pull_error',
	'epic:network:unstable'
].forEach(eventName => window.addEventListener(eventName, handleNetworkIssueEvent));

window.addEventListener('epic:network:online', handleOnlineEvent);
window.addEventListener('epic:network:session_expired', handleSessionExpiredEvent);

export { axios };
